import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const RenameDocumentModal = ({ isOpen, onClose, document, onConfirm }) => {
  const [documentName, setDocumentName] = useState('');
  const [renaming, setRenaming] = useState(false);
  const [error, setError] = useState('');

  // Set initial value when document changes
  useEffect(() => {
    if (document) {
      setDocumentName(document.document_name || '');
      setError('');
    }
  }, [document]);

  if (!isOpen || !document) return null;

  const handleRename = async () => {
    // Validation
    if (!documentName.trim()) {
      setError('Document name cannot be empty');
      return;
    }

    if (documentName.trim() === document.document_name) {
      setError('Please enter a different name');
      return;
    }

    setRenaming(true);
    setError('');

    try {
      await onConfirm(documentName.trim());
      onClose();
    } catch (error) {
      console.error('Rename failed:', error);
      setError(error.message || 'Failed to rename document');
    } finally {
      setRenaming(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !renaming) {
      handleRename();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <FontAwesomeIcon icon="edit" className="text-blue-600" />
            </div>
            <h2 className="text-xl font-semibold text-slate-900">
              Rename Document
            </h2>
          </div>
          <button
            onClick={onClose}
            disabled={renaming}
            className="text-slate-400 hover:text-slate-600 transition-colors"
          >
            <FontAwesomeIcon icon="times" className="text-xl" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Document Name
            </label>
            <input
              type="text"
              value={documentName}
              onChange={(e) => {
                setDocumentName(e.target.value);
                setError('');
              }}
              onKeyPress={handleKeyPress}
              placeholder="Enter new document name"
              disabled={renaming}
              autoFocus
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-slate-100 disabled:cursor-not-allowed"
            />
            {error && (
              <p className="mt-2 text-sm text-red-600 flex items-center space-x-1">
                <FontAwesomeIcon icon="exclamation-circle" />
                <span>{error}</span>
              </p>
            )}
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
            <div className="flex items-start space-x-2">
              <FontAwesomeIcon
                icon="info-circle"
                className="text-slate-500 mt-0.5 flex-shrink-0"
              />
              <div className="text-sm text-slate-600">
                <p className="mb-1">
                  <strong>Current name:</strong> {document.document_name}
                </p>
                <p className="text-xs text-slate-500">
                  Renaming will update the document name across all references.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-end space-x-3">
          <button
            onClick={onClose}
            disabled={renaming}
            className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleRename}
            disabled={renaming || !documentName.trim()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {renaming ? (
              <>
                <FontAwesomeIcon icon="spinner" className="fa-spin" />
                <span>Renaming...</span>
              </>
            ) : (
              <>
                <FontAwesomeIcon icon="check" />
                <span>Rename Document</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RenameDocumentModal;
